import PropTypes from 'prop-types'
import { forwardRef, memo, useMemo } from 'react'
import BorderHighlight from 'components/elements/BorderHighlight/BorderHighlight'
import classNames from 'lib/util/classNames'
import Icon from '../Icon/Icon'
import styles from './Button.module.scss'

/**
 * The `ButtonSymbol`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const ButtonSymbol = forwardRef((props, forwardedRef) => {
  const {
    ElementTag,
    children,
    className,
    icon,
    iconComponent,
    isBusy,
    isHover,
    useHighlight,
    useHighlightHover,
    ...rest
  } = props
  const attributes = useMemo(
    () => ({
      className: classNames([
        styles.button,
        styles.buttonTypeSymbol,
        className
      ])
    }),
    [className]
  )

  const iconOutput = useMemo(() => {
    if (isBusy) {
      return null
    }

    if (iconComponent) {
      return iconComponent
    }

    if (icon) {
      return <Icon icon={icon} />
    }

    return null
  }, [isBusy, iconComponent, icon])

  return (
    <ElementTag ref={forwardedRef} {...attributes} {...rest}>
      {children}
      {(useHighlight || useHighlightHover) && (
        <BorderHighlight
          isActive={useHighlight ? !isHover : isHover}
          animateOnMobile={true}
        />
      )}
      {iconOutput}
    </ElementTag>
  )
})

// Display name
ButtonSymbol.displayName = 'ButtonSymbol'

/** @type {object} */
ButtonSymbol.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  ElementTag: PropTypes.elementType.isRequired,
  icon: PropTypes.string,
  iconComponent: PropTypes.element,
  isBusy: PropTypes.bool,
  isHover: PropTypes.bool,
  useHighlight: PropTypes.bool,
  useHighlightHover: PropTypes.bool
}

/** @type {object} */
ButtonSymbol.defaultProps = {
  children: [],
  className: '',
  iconComponent: null,
  icon: '',
  isBusy: false,
  isHover: false,
  useHighlight: false,
  useHighlightHover: false
}

// Memoize
export default memo(ButtonSymbol)
